/**
 * Einheitliches Datenmodell für alle Check-Report-Typen.
 * Wird von den Adaptern erzeugt und von CheckReportPageComponent konsumiert.
 */

export interface NormalizedReport {
  /** Metadaten für den Overview-Header */
  meta: ReportMeta;
  /** Erste Spalte: Dateien/Gruppen */
  groups: ReportGroup[];
}

export interface ReportMeta {
  checkerName: string;
  checkDate: Date;
  rulesetName: string;
  fileName?: string;
  fileSize?: string;
  fileCount: number;
  ruleCount: number;
  /**
   * Gesamtergebnis in Prozent – oder `null`, wenn NICHTS bewertet wurde.
   *
   * `null` statt einer Zahl ist die Kernaussage: ein Lauf, in dem jedes Ergebnis „unbekannt" oder
   * unbewertet blieb, hatte vorher „50 %" im Bericht und im Export stehen — eine Zahl, die eine
   * halb erfüllte Prüfung behauptete, obwohl nie etwas geprüft wurde. Unbewertete und unbekannte
   * Ergebnisse gehen NICHT in den Durchschnitt ein (siehe {@link ratedCount}).
   */
  score: number | null;
  /** Anzahl Ergebnisse, die ein Prüfergebnis tragen (erfüllt / teilweise / nicht erfüllt). */
  ratedCount?: number;
  /**
   * Hinweis des Prüfmoduls, warum kein bewertbares Ergebnis vorliegt (`score === null`) –
   * z. B. „Keine Spezifikation trifft auf ein Bauteil im Modell zu". Früher endete genau
   * dieser Fall als Fehlschlag ohne Bericht; jetzt existiert der Bericht und nennt die Ursache.
   */
  resultInfo?: string;
  /**
   * Anzahl Ergebnisse ohne Prüfergebnis: ausdrücklich „unbekannt" oder gar unbewertet. Zusammen
   * mit {@link ratedCount} die Grundgesamtheit des Scores — ohne sie ist „100 %" nicht von
   * „100 % von einem einzigen bewerteten Ergebnis" zu unterscheiden.
   */
  unratedCount?: number;
}

export interface ReportGroup {
  name: string;
  /** Ergebnis in Prozent, oder `null` wenn keine Regel dieser Datei bewertet wurde. */
  percent: number | null;
  status: 'success' | 'warning' | 'error' | 'unknown';
  rules: ReportRule[];
  /** Optionales Dateiformat (z. B. „PDF") für die konfigurierbare Spalte „Dateiformat". */
  fileFormat?: string;
  /**
   * Optionale Zwischenebene „Strukturebene" (z. B. LV-Positionen) für die 4-Spalten-Ansicht
   * (Perspektive `structure_first`, v. a. Hybrid). Datei → Struktur → Prüfregeln → Ergebnis.
   * Wird von Perspektiven ohne Strukturebene ignoriert (dann greift `rules`).
   */
  structures?: ReportStructure[];
}

/** Strukturebene (z. B. eine LV-Position) mit den darauf angewandten Prüfregeln. */
export interface ReportStructure {
  name: string;
  /** Ergebnis in Prozent, oder `null` wenn keine Regel dieser Position bewertet wurde. */
  percent: number | null;
  status: 'success' | 'warning' | 'error' | 'unknown';
  rules: ReportRule[];
}

/**
 * Chip-Darstellung (z. B. IDS-Anwendbarkeit / Werte). Zeigt einen Primärwert
 * plus optional die Anzahl weiterer Einträge ("+N"), die per Tooltip o. ä.
 * vollständig vorliegen.
 */
export interface ReportChip {
  /** Optionaler Typ/Präfix des Chips (z. B. Facettentyp-Label "Entität"). */
  label?: string;
  /** Name der Facette (z. B. bei Attribut/Eigenschaft: "GlobalId"). Entität/Material haben keinen. */
  name?: string;
  /** Primär dargestellter Wert. */
  value: string;
  /** Weitere Werte (für "+N" und vollständige Anzeige). */
  rest?: string[];
}

/**
 * Warum eine IDS-Anforderung fehlgeschlagen ist, verdichtet auf einen Eintrag je Grund-Typ.
 *
 * Kommt aus `ids_checker.py` (`failureReasons`) und speist sich aus `ifctester`s
 * `Result.reason` – bis dahin war im Bericht nur „0 %" sichtbar, ohne Ursache. Optional:
 * Berichte, die vor dieser Erweiterung entstanden sind, haben das Feld nicht.
 */
export interface ReportFailureReason {
  /** Maschinenlesbarer Typ: `DATATYPE`, `NOPSET`, `NOVALUE`, `VALUE`, … (vollständige Liste je
   *  Facette bei {@link FAILURE_REASON_LABELS}). */
  type: string;
  /** Deutsche Bezeichnung – siehe {@link resolveFailureReasonLabel}. */
  label: string;
  /** Anzahl Bauteile mit genau diesem Grund. */
  count: number;
  /** Bis zu fünf verschiedene Ist-Werte (im Modul begrenzt, Payload-Grenze). */
  actualValues?: string[];
  /** Soll-Wert; nur beim Datentyp gefüllt. */
  expected?: string;
}

/**
 * Deutsche Labels je Facettentyp und ifctester-Grund-Typ (Quelle: `to_string()` der
 * `*Result`-Klassen in `ifctester/facet.py`, IfcOpenShell). Derselbe Grund-Typ bedeutet je
 * Facette etwas anderes (`NOVALUE` = Attribut fehlt / Eigenschaft fehlt / Material fehlt / …),
 * ein einzelnes facettenunabhängiges Label wäre daher meist irreführend.
 */
const FAILURE_REASON_LABELS: Record<string, Record<string, string>> = {
  entity: {
    NAME: 'Falsche IFC-Klasse',
    PREDEFINEDTYPE: 'Falscher PredefinedType',
  },
  attribute: {
    NOVALUE: 'Attribut nicht vorhanden',
    FALSEY: 'Attribut ist leer',
    INVALID: 'Ungültiger Attributname in der IDS',
    VALUE: 'Attributwert weicht ab',
    PROHIBITED: 'Attribut erfüllt eine verbotene Anforderung',
  },
  classification: {
    NOVALUE: 'Keine Klassifikation vorhanden',
    VALUE: 'Klassifikationswert weicht ab',
    SYSTEM: 'Klassifikationssystem weicht ab',
    PROHIBITED: 'Klassifikation erfüllt eine verbotene Anforderung',
  },
  partOf: {
    NOVALUE: 'Keine Beziehung vorhanden',
    ENTITY: 'Beziehung zu falscher Entität',
    PREDEFINEDTYPE: 'Beziehung zu falschem PredefinedType',
    PROHIBITED: 'Beziehung erfüllt eine verbotene Anforderung',
  },
  property: {
    NOPSET: 'PropertySet nicht vorhanden',
    // Nicht "... im PropertySet nicht vorhanden": das Pset ist in IDS optional ("egal, welches"),
    // das würde bei "Pset egal" ein konkretes Pset unterstellen, das es gar nicht gibt.
    NOVALUE: 'Eigenschaft nicht vorhanden',
    DATATYPE: 'Datentyp weicht ab',
    VALUE: 'Eigenschaftswert weicht ab',
    PROHIBITED: 'Eigenschaft erfüllt eine verbotene Anforderung',
  },
  material: {
    NOVALUE: 'Kein Material vorhanden',
    VALUE: 'Materialangabe weicht ab',
    PROHIBITED: 'Material erfüllt eine verbotene Anforderung',
  },
};

/**
 * Ordnet ein Facetten-Label einem Schlüssel aus {@link FAILURE_REASON_LABELS} zu. Per Teilstring,
 * da das Prüfmodul Umlaute unterschiedlich schreibt ("Entitaet") und die Facette teils deutsch,
 * teils als englischer Rohschlüssel ("Entity") ankommt.
 */
function facetReasonKey(facetLabel: string | undefined): keyof typeof FAILURE_REASON_LABELS | null {
  const normalized = (facetLabel ?? '').toLowerCase()
    .replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss');
  if (normalized.includes('eigenschaft') || normalized.includes('property')) return 'property';
  if (normalized.includes('attribut')) return 'attribute';
  if (normalized.includes('klassifi') || normalized.includes('classif')) return 'classification';
  if (normalized.includes('material')) return 'material';
  if (normalized.includes('teil von') || normalized.includes('partof') || normalized.includes('part_of')) return 'partOf';
  if (normalized.includes('entit')) return 'entity'; // deckt auch "Entitätengruppe" ab
  return null;
}

/**
 * Löst das facettenkorrekte Label eines Fehlschlag-Grunds auf; gewinnt gegenüber dem vom
 * Prüfmodul gelieferten Label. Fällt bei unbekannter Facette/Typ auf `fallbackLabel` zurück.
 */
export function resolveFailureReasonLabel(
  facetLabel: string | undefined, type: string, fallbackLabel: string,
): string {
  const key = facetReasonKey(facetLabel);
  const label = key ? FAILURE_REASON_LABELS[key][type] : undefined;
  return label ?? fallbackLabel;
}

/**
 * Ein Fehlergrund als Klartext, z. B. „Datentyp weicht ab (46 Bauteile): IfcLabel erwartet,
 * IfcText gefunden".
 *
 * Zentral, weil Spalten- und Kartenansicht denselben Satz zeigen müssen – sonst laufen die zwei
 * parallelen Renderer auseinander.
 */
export function formatFailureReason(reason: ReportFailureReason): string {
  const anzahl = reason.count === 1 ? '1 Bauteil' : `${reason.count} Bauteile`;
  let text = `${reason.label} (${anzahl})`;

  const details: string[] = [];
  if (reason.expected) details.push(`${reason.expected} erwartet`);
  const ist = (reason.actualValues ?? []).join(', ');
  if (ist) details.push(`${ist} gefunden`);
  if (details.length > 0) text += `: ${details.join(', ')}`;

  return text;
}

export interface ReportRule {
  name: string;
  /**
   * Ergebnis in Prozent, oder `null` wenn die Regel kein bewertetes Teilergebnis hat (alles
   * unbekannt/unbewertet). `0` heißt „nicht erfüllt", `null` heißt „nicht bewertet" – diese zwei
   * Fälle waren vorher nicht unterscheidbar.
   */
  percent: number | null;
  status: 'success' | 'warning' | 'error' | 'unknown';
  details: ReportDetail[];
  /** Optionales Prüfregelkürzel für die konfigurierbare Spalte „Prüfregelkürzel". */
  shortCode?: string;
  /**
   * Optionaler Beschreibungstext der Prüfregel (z. B. „Jede Ordnungszahl genau einmal."). Steht
   * wie das Kürzel nicht im Prüfergebnis, sondern nur in der Regelset-Definition und wird
   * nachgeladen (siehe `CheckReportPageComponent.applyRuleMetadata`). In der Kartenansicht ist er
   * die graue Sekundärzeile im Karten-Header, in der Spaltenansicht die Spalte „Beschreibung".
   */
  description?: string;
  /** IDS: Anwendbarkeit der Spezifikation (Entität/Attribut/… als Chips). */
  applicabilities?: ReportChip[];
  /** IDS: Anzahl betroffener/anwendbarer Bauteile der Spezifikation. */
  elementCount?: number;
  /** IDS: true, wenn die Anwendbarkeit auf kein Bauteil im Modell zutrifft (0 Bauteile gefunden). */
  notApplicable?: boolean;
}

/** Statusverteilung (in %) einer Item-Liste für die Overview-Kreise. */
export interface StatusSummary {
  completed: number;
  warning: number;
  error: number;
}

/**
 * Zählt Items nach Status und gibt die prozentuale Verteilung zurück.
 * Wird für die Overview-Kreise genutzt, damit diese exakt aus denselben Items
 * abgeleitet werden wie die Tabelle (keine Divergenz zwischen Übersicht und Liste).
 */
export function summarizeStatus(
  items: ReadonlyArray<{ status: 'success' | 'warning' | 'error' | 'unknown' }>
): StatusSummary {
  const total = items.length;
  if (total === 0) return {completed: 0, warning: 0, error: 0};
  const share = (status: 'success' | 'warning' | 'error' | 'unknown') =>
    (items.filter(item => item.status === status).length / total) * 100;
  return {completed: share('success'), warning: share('warning'), error: share('error')};
}

/** Absolute Statusanzahlen einer Item-Liste. */
export interface StatusCounts {
  success: number;
  warning: number;
  error: number;
  unknown: number;
}

/**
 * Zählt Items nach Status (absolut). Schwesterfunktion zu {@link summarizeStatus}, die dieselben
 * Items prozentual verteilt: die Overview-Kreise zeigen Prozente, die Zähl-Badges der
 * Kartenansicht („5 erfüllt / 2 teilweise erfüllt / 3 nicht erfüllt / 1 unbekannt") Anzahlen.
 */
export function countStatus(
  items: ReadonlyArray<{ status: 'success' | 'warning' | 'error' | 'unknown' }>
): StatusCounts {
  const counts: StatusCounts = {success: 0, warning: 0, error: 0, unknown: 0};
  for (const item of items) counts[item.status]++;
  return counts;
}

/**
 * Eine vom Prüfmodul herangezogene Quelldatei einer KI-Antwort (`source_files`).
 *
 * `id` ist die echte `ProjectFile.id` – sie stammt nicht aus dem Antworttext des Modells, sondern
 * aus den Retrieval-Metadaten (`dataId` der genutzten Fragmente, siehe `source_files_of()` im
 * flowBIC-Modul `ai_check`). Damit ist sie als Linkziel belastbar; die vom Modell im Text genannten
 * Namen sind es nicht.
 */
export interface ReportSourceFile {
  id: string;
  name: string;
}

export interface ReportDetail {
  type: 'success' | 'warning' | 'error' | 'unknown';
  /**
   * KI: prägnante Überschrift des Prüfschritts (vom Modell generiert, ohne "Schritt N:"-Präfix),
   * z. B. "Identifikation der Dokumente". Wird als Überschrift des Prüfschritt-Kastens verwendet.
   */
  heading?: string;
  /** Kurzbeschreibung des Ergebnisses */
  text: string;
  /** Anzahl betroffener Bauteile */
  count: number;
  /** IDS: Facettentyp-Label (z. B. "Entität", "Attribut", "Eigenschaft"). */
  facetLabel?: string;
  /** IDS: Name der Facette (z. B. "Türnummer"). */
  name?: string;
  /** IDS: Werte der Facette als Chips ("Länge Min 4" + "+N"). */
  valueChips?: ReportChip[];
  /** IDS: Ergebnis der Facette in Prozent (0–100). */
  percent?: number;
  /**
   * Vollständig dynamische, benutzerdefinierte KI-Ausgabefelder
   * (key = Name des konfigurierten Ausgabeparameters, z. B. "Bewertung", "Antwort").
   * Wird vom Berichtsview anhand der konfigurierten Spalten dynamisch dargestellt.
   */
  fields?: Record<string, any>;
  /**
   * KI: die Dateien, aus denen die Antwort tatsächlich Inhalt bezogen hat.
   *
   * Nur im gemeinsamen Prüflauf (Prüfmodus `COMBINED`) belegt – nur dort stützt sich eine Antwort
   * auf mehrere Dateien und nennt sie im Begründungstext. Einträge ohne `id` (die Platzhalter
   * „Ohne Dateibezug"/„Keine passende Datei" des Aggregators) sind hier bereits aussortiert, damit
   * daraus kein toter Link entsteht.
   */
  sourceFiles?: ReportSourceFile[];
  /** Raw JSON-Output des Prüfregeldurchlaufs (für Hybrid/AI) */
  rawOutput?: any;
  /**
   * IDS: warum die Anforderung fehlgeschlagen ist. Fehlt bei erfüllten Anforderungen und bei
   * Berichten von vor dieser Erweiterung – die Darstellung muss ohne das Feld unverändert
   * funktionieren.
   */
  failureReasons?: ReportFailureReason[];
}
